import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { getUsers, createUser, updateUser, deleteUser, User } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useResponsive } from '../hooks/useResponsive';

const Users: React.FC = () => {
  const { user: currentUser, loading: authLoading, loginAsUser } = useAuth();
  const { isMobile } = useResponsive();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({});

  const filteredUsers = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const fields = [u.email, u.fname, u.lname, u.phone, u.nif, u.status];
      return fields.some((f) => (f || '').toString().toLowerCase().includes(q));
    });
  }, [users, search]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers(1, 100);
      const usersData = response.data?.data || [];
      
      console.log('📊 Fetched users data:', usersData);
      console.log('📊 Sample user is_admin values:', usersData.map((user: any) => ({
        email: user.email,
        is_admin: user.is_admin,
        type: typeof user.is_admin
      })));
      console.log('📊 Sample user name fields:', usersData.slice(0, 3).map((user: any) => ({
        email: user.email,
        fname: user.fname,
        lname: user.lname,
        fnameType: typeof user.fname,
        lnameType: typeof user.lname
      })));
      console.log('📊 Full first user object:', usersData[0]);
      
      setUsers(usersData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load users';
      setError(errorMessage);
      console.error('Users error:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({});
    setOpenDialog(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    // Format the date for the date input field and ensure boolean conversion
    const formattedUser = {
      ...user,
      dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : '',
      is_admin: Boolean(user.is_admin)
    };
    setFormData(formattedUser);
    setOpenDialog(true);
  };

  const handleDeleteUser = async (email: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await deleteUser(email);
        fetchUsers();
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Failed to delete user';
        setError(errorMessage);
      }
    }
  };

  const handleLoginAs = async (email: string) => {
    try {
      const success = await loginAsUser(email);
      if (success) {
        // The AuthContext will handle the state update
        console.log(`Successfully logged in as ${email}`);
        // Refresh the current page to reflect the new user context
        window.location.reload();
      } else {
        setError('Failed to login as user');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to login as user';
      setError(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      if (editingUser) {
        await updateUser(editingUser.email, formData);
      } else {
        await createUser(formData as User);
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to save user';
      setError(errorMessage);
    }
  };

  console.log('🔍 Users page - isMobile:', isMobile, 'window width:', window.innerWidth);
  
  const columns: GridColDef[] = isMobile ? [
    { 
      field: 'name', 
      headerName: 'Name', 
      width: 200,
      renderCell: (params) => {
        if (!params || !params.row) return null;
        
        // Compute the name directly from fname and lname
        const fname = params.row.fname || '';
        const lname = params.row.lname || '';
        const displayName = `${fname} ${lname}`.trim() || 'No Name';
        
        console.log('🔍 Mobile Name renderCell:', { 
          email: params.row.email,
          fname: params.row.fname,
          lname: params.row.lname,
          computedName: displayName
        });
        
        return (
          <Box
            onClick={() => handleEditUser(params.row)}
            sx={{ 
              cursor: 'pointer',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '8px 12px',
              boxSizing: 'border-box',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </span>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditUser(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteUser(params.row.email)}
          />,
        ];
      },
    },
  ] : [
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'fname', headerName: 'First Name', width: 120 },
    { field: 'lname', headerName: 'Last Name', width: 120 },
    { field: 'phone', headerName: 'Phone', width: 150 },
    { field: 'nif', headerName: 'NIF', width: 120 },
    { field: 'status', headerName: 'Status', width: 100 },
    {
      field: 'is_admin',
      headerName: 'Admin',
      width: 100,
      type: 'boolean',
      renderCell: (params: any) => {
        console.log('🎨 renderCell params:', {
          row: params.row,
          field: params.field,
          value: params.value,
          is_admin: params.row?.is_admin,
          type: typeof params.row?.is_admin
        });
        
        // Get the raw value from the row data
        const rawValue = params.row.is_admin;
        const isAdmin = Boolean(rawValue);
        
        console.log('🎨 renderCell conversion:', {
          rawValue,
          isAdmin,
          rawType: typeof rawValue
        });
        
        return (
          <Box sx={{ 
            backgroundColor: isAdmin ? 'success.main' : 'grey.300',
            color: isAdmin ? 'white' : 'text.primary',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}>
            {isAdmin ? 'Admin' : 'User'}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 180,
      getActions: (params) => {
        if (!params || !params.row) return [];
        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            onClick={() => handleEditUser(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={() => handleDeleteUser(params.row.email)}
          />,
          // Only show "Login As" for admin users and not for themselves
          ...(currentUser && currentUser.is_admin && params.row.email !== currentUser.email ? [
            <GridActionsCellItem
              icon={<PersonIcon color="primary" />}
              label="Login As"
              onClick={() => handleLoginAs(params.row.email)}
            />
          ] : []),
        ];
      },
    },
  ];

  console.log('🔍 Columns being used:', columns.map(col => ({ field: col.field, headerName: col.headerName })));

  // Don't render until authentication is complete
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} gap={2} flexWrap="wrap">
        <Typography variant="h4">Users Management</Typography>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search email, name, phone, NIF, status"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="admin-mobile-search admin-desktop-search"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddUser}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={() => setError('')}
          action={
            <Button color="inherit" size="small" onClick={() => setError('')}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <Box sx={{ height: 600, width: '100%' }} className="admin-mobile-table">
        <DataGrid
          rows={filteredUsers || []}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.email}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          columnVisibilityModel={isMobile ? {
            name: true,
            actions: true,
            // Explicitly hide all other possible columns
            email: false,
            fname: false,
            lname: false,
            phone: false,
            nif: false,
            status: false,
            is_admin: false,
            dob: false,
            address: false
          } : {
            email: true,
            fname: true,
            lname: true,
            phone: true,
            nif: true,
            status: true,
            is_admin: true,
            name: false,
            actions: true,
            dob: false,
            address: false
          }}
          disableColumnMenu={true}
          disableColumnFilter={true}
          disableColumnSelector={true}
          disableColumnResize={true}
        />
      </Box>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={formData.fname || ''}
              onChange={(e) => setFormData({ ...formData, fname: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={formData.lname || ''}
              onChange={(e) => setFormData({ ...formData, lname: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Date of Birth"
              type="date"
              value={formData.dob || ''}
              onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Phone"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="NIF"
              value={formData.nif || ''}
              onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Address"
              value={formData.address || ''}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
              sx={{ gridColumn: 'span 2' }}
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              helperText={editingUser ? "Leave blank to keep current password" : "Required for new users"}
              required={!editingUser}
            />
            {/* Admin toggle - only show for admin users */}
            {currentUser && currentUser.is_admin && (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(formData.is_admin)}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                    color="success"
                  />
                }
                label="Admin User"
                sx={{ 
                  gridColumn: 'span 2',
                  mt: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  backgroundColor: 'background.paper'
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingUser ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
